import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Loader2, Archive, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Admin card: pick the audit-log retention window (30/60/90/180/365 days).
 * Backend prunes daily; this card also offers a one-click "Prune now".
 */
export function AuditRetentionCard({ api }) {
  const [cfg, setCfg] = useState({ days: 60, allowed_days: [30, 60, 90, 180, 365], default_days: 60 });
  const [selected, setSelected] = useState(60);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pruning, setPruning] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/api/admin/audit-retention/config');
      setCfg(data);
      setSelected(data.days);
    } catch (e) { /* non-fatal */ }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/api/admin/audit-retention/config', { days: selected });
      setCfg(data);
      toast.success(`Retention window set to ${data.days} days`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const pruneNow = async () => {
    if (!window.confirm(`Hard-delete every audit log older than ${cfg.days} days right now?`)) return;
    setPruning(true);
    try {
      const { data } = await api.post('/api/admin/audit-retention/prune-now');
      toast.success(`Pruned ${data.pruned} log(s)`);
    } catch (e) {
      toast.error('Prune failed');
    }
    setPruning(false);
  };

  return (
    <Card className="bg-[#041A3B] border-white/10" data-testid="audit-retention-card">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Archive className="w-5 h-5 text-[#00E5FF]" /> Audit Log Retention
        </CardTitle>
        <CardDescription className="text-[#A0B4CC]">
          Choose how long activity logs are kept. The daily prune runs in the background.
          Currently: <strong className="text-white">{cfg.days} days</strong>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="text-[#A0B4CC] text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            <div>
              <Label className="text-[#A0B4CC] text-xs">Retention window</Label>
              <select
                value={selected}
                onChange={(e) => setSelected(parseInt(e.target.value, 10))}
                className="w-full h-10 px-3 rounded-md bg-[#000B18] border border-white/20 text-white text-sm focus:border-[#00E5FF] focus:outline-none"
                data-testid="audit-retention-select"
              >
                {(cfg.allowed_days || []).map((d) => (
                  <option key={d} value={d}>{d} days</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={save} disabled={saving || selected === cfg.days}
                className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90"
                data-testid="audit-retention-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
              </Button>
              <Button onClick={pruneNow} disabled={pruning} variant="outline"
                className="border-[#FF1744]/40 text-[#FF1744] hover:bg-[#FF1744]/10"
                data-testid="audit-retention-prune-now">
                {pruning ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4 mr-1.5" /> Prune Now</>}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
